/**
 * Zmiana NUMERÓW lokali (prośba właściciela 2026-10-21, Wolności 532c: lokale 1–10 → 31–40). W przeciwieństwie do
 * oznaczeń E3 (utils/apartmentLabels.ts — tylko wyświetlanie) zmienia się numer lokalu, czyli tożsamość w CEEB.
 * Czyste funkcje dla ApartmentRenumberModal; reguły zapisu i bezpieczniki są na serwerze
 * (backend/src/utils/apartmentRenumber.js — lustro formatu numeru: `apartmentNumberError`).
 */
import { MAX_LABEL_RANGE, plainApartmentNumber } from './apartmentLabels';

export const APARTMENT_NUMBER_MAX = 50; // VARCHAR(50)

const LETTER = '0-9A-Za-zĄĆĘŁŃÓŚŹŻąćęłńóśźż';
const NUMBER_RE = new RegExp(`^[${LETTER}](?:[${LETTER} /.-]*[${LETTER}])?$`);

/** Klucz porównań unikalności (jak serwer): przycięty, małe litery. */
export const numberKey = (v: string | null | undefined) => String(v ?? '').trim().toLowerCase();

/** Błąd formatu nowego numeru albo null (lustro backendu). */
export function apartmentNumberError(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Numer nie może być pusty';
  if (v.length > APARTMENT_NUMBER_MAX) return `Najwyżej ${APARTMENT_NUMBER_MAX} znaków`;
  if (/\s{2,}/.test(v) || !NUMBER_RE.test(v)) return 'Dozwolone litery, cyfry, spacja oraz „/", „-", „." (np. 31, 13a, 22/27b)';
  return null;
}

export interface RenumberRangeInput {
  /** Obecne numery „od–do" (włącznie). */
  from: number;
  to: number;
  /** Nowy numer lokalu `from`; kolejne lokale z przesunięciem (lokal from+k → start+k). */
  start: number;
}

export type RenumberRangePlan =
  | { error: string }
  | { rows: { apartmentId: string; from: string; to: string }[]; skipped: string[] };

/**
 * „Lokale od–do → od N": lokal `from + k` dostaje numer `start + k` (brakujący lokal nie przesuwa reszty). Lokale
 * z nietypowym numerem („13a", „22/27b") w zakresie nie wchodzą — `skipped` (do zmiany w wierszu).
 */
export function planRenumberRange(
  apartments: readonly { id: string; apartmentNumber: string }[],
  input: RenumberRangeInput,
): RenumberRangePlan {
  const { from, to, start } = input;
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < 0) return { error: 'Podaj obecne numery lokali „od" i „do" (liczby całkowite).' };
  if (to < from) return { error: '„Do" nie może być mniejsze niż „od".' };
  if (to - from + 1 > MAX_LABEL_RANGE) return { error: `Najwięcej ${MAX_LABEL_RANGE} lokali naraz.` };
  if (!Number.isInteger(start) || start < 1) return { error: 'Nowe numery „od" muszą być liczbą całkowitą większą od zera.' };
  const rows: { apartmentId: string; from: string; to: string }[] = [];
  const skipped: string[] = [];
  for (const a of apartments) {
    const n = plainApartmentNumber(a.apartmentNumber);
    if (n === null) {
      const lead = /^\s*(\d+)/.exec(a.apartmentNumber || '');
      if (lead && Number(lead[1]) >= from && Number(lead[1]) <= to) skipped.push(a.apartmentNumber.trim());
      continue;
    }
    if (n < from || n > to) continue;
    rows.push({ apartmentId: a.id, from: a.apartmentNumber, to: String(start + (n - from)) });
  }
  rows.sort((x, y) => Number(x.from) - Number(y.from));
  return { rows, skipped };
}

/**
 * Błędy wierszy po stronie panelu (zanim pójdzie podgląd): format i unikalność stanu PO zmianie — zamiana/przesunięcie
 * są dozwolone (liczy się wynik), kolizja z lokalem, który zostaje, nie. Klucz = id lokalu.
 */
export function renumberDraftErrors(
  apartments: readonly { id: string; apartmentNumber: string }[],
  drafts: Record<string, string>,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const finalNr = (a: { id: string; apartmentNumber: string }) => {
    const d = drafts[a.id];
    return d !== undefined && d.trim() !== '' ? d.trim() : a.apartmentNumber;
  };
  const owners = new Map<string, string[]>();
  for (const a of apartments) {
    const d = drafts[a.id];
    if (d !== undefined && d.trim() !== '' && d.trim() !== a.apartmentNumber) {
      const err = apartmentNumberError(d);
      if (err) errors[a.id] = err;
    }
    const k = numberKey(finalNr(a));
    owners.set(k, [...(owners.get(k) || []), a.id]);
  }
  for (const a of apartments) {
    if (errors[a.id]) continue;
    const changed = finalNr(a) !== a.apartmentNumber;
    const list = owners.get(numberKey(finalNr(a))) || [];
    if (changed && list.length > 1) {
      const other = apartments.find((x) => x.id !== a.id && list.includes(x.id));
      errors[a.id] = other && finalNr(other) === other.apartmentNumber
        ? `Numer ${finalNr(a)} ma już lokal ${other.apartmentNumber} — dodaj go do zmiany albo wybierz inny numer`
        : `Numer ${finalNr(a)} wpisany dla dwóch lokali`;
    }
  }
  return errors;
}
